// ScopeSelector — top-of-tab segmented control that lets the user choose
// what slice of work to look at on a contract: All (mixed), Contract
// (items not pinned to any SOW), or a specific SOW. The SOW segment
// doubles as a dropdown trigger; clicking it switches scope to SOW mode
// and opens the picker, populated with active SOWs (archived or
// cancelled excluded — same rule as the deliverable form picker and the
// SOW filter chip). Chosen SOW is shown on the segment label as
// `SOW: SOW-2026-001 ⌄`. Empty active-SOW list still renders the
// segment but the dropdown shows a non-interactive "No active SOWs"
// row so the user knows why they cannot pick one.
//
// Visually mirrors the existing SegmentedControl (slate-100 container,
// white shadow on the active segment) so it reads as a single primitive
// alongside other segmented controls in the app. Brand discipline:
// slate neutrals + brand-* for active focus rings, text-body-sm.

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { clsx } from "clsx";
import type { Sow } from "../../../../types";
import type { ScopeValue } from "./scopeTypes";
import {
  SEGMENT_CONTAINER,
  SEGMENT_BASE,
  SEGMENT_ACTIVE,
  SEGMENT_INACTIVE,
} from "./segmentClasses";
import {
  SOW_SEARCH_THRESHOLD,
  matchesSowSearch,
  compareSowNumbers,
} from "./sowPickerSearch";

export interface ScopeSelectorProps {
  scope: ScopeValue;
  onScopeChange: (next: ScopeValue) => void;
  sows: readonly Sow[];
  /** Used in aria-labels and per-button data-testid suffixes. */
  itemNoun: "deliverable" | "milestone";
}

// Fixed comfortable width instead of shrink-to-fit — prod SOW names run
// long and wrap into an unreadable sliver at the old 240px minimum.
// Viewport-capped so it never overflows small screens. Anchored left-0:
// the scope selector sits at the left of the content column, so a
// right-anchored popover grows leftwards over the sidebar and off the
// viewport once it's wider than the segment control.
const POPOVER_CLASS =
  "absolute left-0 z-20 mt-1 w-96 max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800";

function selectedSowOf(
  scope: ScopeValue,
  sows: readonly Sow[],
): Sow | null {
  if (scope.mode !== "sow") return null;
  return sows.find((s) => s.id === scope.sowId) ?? null;
}

export default function ScopeSelector({
  scope,
  onScopeChange,
  sows,
  itemNoun,
}: ScopeSelectorProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sowSearch, setSowSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Active SOWs only — same rule used by the form picker and the
  // FilterChipBar SOW chip. Natural-ascending number order for
  // predictable scanning (numeric-aware, so 2 < 10).
  const activeSows = useMemo(
    () =>
      [...sows]
        .filter((s) => !s.archivedAt && s.status !== "cancelled")
        .sort((a, b) => compareSowNumbers(a.number, b.number)),
    [sows],
  );

  const searchable = activeSows.length > SOW_SEARCH_THRESHOLD;
  const visibleSows = searchable
    ? activeSows.filter((s) =>
        matchesSowSearch(`${s.number} · ${s.name}`, sowSearch),
      )
    : activeSows;

  // Reopening starts unfiltered — a stale query from the last visit
  // would silently hide options. Render-time prev-value guard (not an
  // effect) per the react-hooks set-state-in-effect rule.
  const [prevPickerOpen, setPrevPickerOpen] = useState(pickerOpen);
  if (prevPickerOpen !== pickerOpen) {
    setPrevPickerOpen(pickerOpen);
    if (!pickerOpen) setSowSearch("");
  }

  const selectedSow = selectedSowOf(scope, sows);

  useEffect(() => {
    if (!pickerOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPickerOpen(false);
    };
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [pickerOpen]);

  const handleAll = () => {
    setPickerOpen(false);
    if (scope.mode !== "all") onScopeChange({ mode: "all" });
  };

  const handleContract = () => {
    setPickerOpen(false);
    if (scope.mode !== "contract") onScopeChange({ mode: "contract" });
  };

  // SOW segment: clicking switches to SOW mode AND toggles the picker.
  // When already in SOW mode with a chosen SOW, clicking just toggles
  // the picker so the user can change the selection.
  const handleSowSegment = () => {
    setPickerOpen((prev) => !prev);
  };

  const handlePickSow = (sowId: string) => {
    setPickerOpen(false);
    if (scope.mode !== "sow" || scope.sowId !== sowId) {
      onScopeChange({ mode: "sow", sowId });
    }
  };

  const sowSegmentActive = scope.mode === "sow";
  const sowLabel = selectedSow
    ? `SOW: ${selectedSow.number}`
    : "SOW";

  return (
    <div ref={containerRef} className="relative inline-block">
      <div
        role="group"
        aria-label={`${itemNoun} scope`}
        data-testid={`${itemNoun}-scope-selector`}
        className={SEGMENT_CONTAINER}
      >
        <button
          type="button"
          onClick={handleAll}
          aria-pressed={scope.mode === "all"}
          data-testid={`${itemNoun}-scope-all`}
          className={clsx(
            SEGMENT_BASE,
            scope.mode === "all" ? SEGMENT_ACTIVE : SEGMENT_INACTIVE,
          )}
        >
          All
        </button>
        <button
          type="button"
          onClick={handleContract}
          aria-pressed={scope.mode === "contract"}
          data-testid={`${itemNoun}-scope-contract`}
          className={clsx(
            SEGMENT_BASE,
            scope.mode === "contract" ? SEGMENT_ACTIVE : SEGMENT_INACTIVE,
          )}
        >
          Contract
        </button>
        <button
          type="button"
          onClick={handleSowSegment}
          aria-pressed={sowSegmentActive}
          aria-haspopup="listbox"
          aria-expanded={pickerOpen}
          data-testid={`${itemNoun}-scope-sow`}
          className={clsx(
            SEGMENT_BASE,
            "inline-flex items-center gap-1",
            sowSegmentActive ? SEGMENT_ACTIVE : SEGMENT_INACTIVE,
          )}
        >
          <span className="max-w-[180px] truncate">{sowLabel}</span>
          <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
      </div>

      {pickerOpen && (
        <div
          data-testid={`${itemNoun}-scope-sow-picker`}
          role="listbox"
          aria-label={`Choose SOW for ${itemNoun} scope`}
          className={POPOVER_CLASS}
        >
          {searchable && (
            <input
              type="search"
              className="input mb-1 w-full"
              placeholder="Search SOWs…"
              aria-label="Search SOWs"
              value={sowSearch}
              onChange={(e) => setSowSearch(e.target.value)}
              data-testid={`${itemNoun}-scope-sow-search`}
            />
          )}
          {activeSows.length === 0 ? (
            <div
              data-testid={`${itemNoun}-scope-sow-picker-empty`}
              className="px-2 py-1.5 text-body-sm text-slate-500 dark:text-slate-400"
            >
              No active SOWs
            </div>
          ) : visibleSows.length === 0 ? (
            <div
              data-testid={`${itemNoun}-scope-sow-search-empty`}
              className="px-2 py-1.5 text-caption text-slate-500 dark:text-slate-400"
            >
              No SOWs match &ldquo;{sowSearch.trim()}&rdquo;.
            </div>
          ) : (
            <ul className="space-y-0.5">
              {visibleSows.map((s) => {
                const isSelected =
                  scope.mode === "sow" && scope.sowId === s.id;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handlePickSow(s.id)}
                      data-testid={`${itemNoun}-scope-sow-option-${s.id}`}
                      className={clsx(
                        "flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-body-sm",
                        "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400",
                        isSelected
                          ? "bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300"
                          : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700",
                      )}
                    >
                      <span className="font-semibold">{s.number}</span>
                      <span className="min-w-0 text-slate-500 dark:text-slate-400">
                        · {s.name}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
